import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import {
  collection,
  query,
  orderBy,
  startAfter,
  limit,
  getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Timestamp } from 'firebase/firestore';
import { useRecordContext } from 'react-admin';
import { useEffect, useState } from 'react';


const columns: GridColDef[] = [
  { field: 'tokens', headerName: 'Tokens', width: 130 },
  {
    field: 'transaction_date',
    headerName: 'Transaction Date',
    width: 200,
    renderCell: (params) => {
      const v = params.value;
      if (!v) return '';
      // If it's a Firestore Timestamp, convert to Date
      if ((v as any).toDate instanceof Function) {
        return (v as Timestamp).toDate().toLocaleString();
      }
      // Otherwise assume it's already a date string or number
      return new Date(v as string | number).toLocaleString();
    },
  },
  { field: 'provider', headerName: 'Provider', width: 150 },
  { field: 'amount_total_usd', headerName: 'Total (USD)', width: 130 },
  { field: 'amount_tax_usd', headerName: 'Tax (USD)', width: 120 },
  { field: 'amount_tax_percentage', headerName: 'Tax %', width: 100 },
  { field: 'transaction_fixed_fee', headerName: 'Fixed Fee', width: 120 },
  { field: 'transaction_percentage_fee', headerName: 'Fee %', width: 100 },
  { field: 'provider_transaction_fee', headerName: 'Provider Fee', width: 130 },
  { field: 'net_payout_amount', headerName: 'Net Payout', width: 130 },
  { field: 'transaction_status', headerName: 'Status', width: 120 },
  { field: 'provider_ref', headerName: 'Provider Ref', width: 200 },
  {
    field: 'id',
    headerName: 'Reference',
    width: 200,
    renderCell: (params) => {
      return params.row?.id || '';
    },
  },
];

export const Payouts = () => {
  const record = useRecordContext();
  const userId = record?.id?.toString();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'transaction_date', sort: 'desc' },
  ]);

  useEffect(() => {
    setLastVisible(null);
  }, [sortModel, page]);

  useEffect(() => {
    if (!userId) return;
    const fetchPage = async () => {
      setLoading(true);
      // Count total
      try {
        const countSnap = await getCountFromServer(
          query(collection(db, 'users', userId, 'payouts'))
        );
        setRowCount(countSnap.data().count);
      } catch { /* ignore count errors */ }

      // Build base collection reference
      const baseCollection = collection(db, 'users', userId, 'payouts');
      const activeSort = sortModel[0] || { field: 'transaction_date', sort: 'desc' };
      const sortField = activeSort.field;
      const sortDirection = (activeSort.sort ?? 'desc').toLowerCase() as 'asc' | 'desc';
      let qRef =
        page > 0 && lastVisible
          ? query(baseCollection, orderBy(sortField, sortDirection), startAfter(lastVisible), limit(pageSize))
          : query(baseCollection, orderBy(sortField, sortDirection), limit(pageSize));

      const snap = await getDocs(qRef);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setRows(docs);
      setLastVisible(snap.docs[snap.docs.length - 1] || null);
      setLoading(false);
    };
    fetchPage();
  }, [userId, page, pageSize, sortModel, lastVisible]);

  if (loading && rows.length === 0) return <p>Loading...</p>;

  return (
    <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            rowCount={rowCount}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            loading={loading}
          />
        </div>
  );
};